import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiGet, apiPost, errOf, reasonOf } from '../lib/api';

export interface AuthUser {
  id: string;
  email: string;
  role: string;
  status: string;
  has_password?: boolean;
}

interface AuthValue {
  user: AuthUser | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<{ ok: boolean; error: string; reason?: string }>;
  loginWithToken: (token: string) => Promise<{ ok: boolean; error: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** 認證狀態（開頁即問 `/api/auth/me`；login／logout 後刷新） */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const r = await apiGet<{ user: AuthUser }>('/api/auth/me');
    setUser(r.ok && r.data ? r.data.user : null);
  }, []);

  useEffect(() => {
    void loadMe().finally(() => setLoading(false));
  }, [loadMe]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      async login(identifier, password) {
        // 有 '@' → email（職員，要密碼）；否則電話號碼／會員編號（會員，免密碼）
        const body = identifier.includes('@') ? { email: identifier, password } : { phone: identifier };
        const r = await apiPost<{ user?: AuthUser }>('/api/auth/login', body);
        if (r.ok) {
          const me = await apiGet<{ user: AuthUser }>('/api/auth/me');
          setUser(me.ok && me.data ? me.data.user : null);
          return { ok: true, error: '' };
        }
        /** `reason` 交畀 UI 決定流程（例：`not_member` → 直接跳登記頁） */
        return { ok: false, error: errOf(r.data), reason: reasonOf(r.data) };
      },
      async loginWithToken(token) {
        const r = await apiPost('/api/auth/token-login', { token });
        if (r.ok) {
          const me = await apiGet<{ user: AuthUser }>('/api/auth/me');
          setUser(me.ok && me.data ? me.data.user : null);
          return { ok: true, error: '' };
        }
        return { ok: false, error: errOf(r.data) };
      },
      async logout() {
        await apiPost('/api/auth/logout', {});
        setUser(null);
      },
      refresh: loadMe,
    }),
    [user, loading, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth 必須在 <AuthProvider> 內使用');
  return v;
}
